import { useRecoilValue, useSetRecoilState } from "recoil"
import { localUserCallbackSelector, userIdsSelector } from "../recoil/atoms/users"
import { screenShareUserSelector } from "../recoil/atoms/screenShareUser"
import {
  ConnectingResponse,
  NackRemoteStatusChangeResponse,
  UplinkStatusChangeResponse,
  PublishingResponse
} from "./types"
import { ConsoleLogger } from "../util/logger"
import chroma from "chroma-js"
import { activeSpeakerSelector } from "../recoil/atoms/activeSpeaker"
import { joinButtonLoadingSelector } from "../recoil/atoms/joinButton"
import { IS_RECORDING_VIEW, Views } from "../constants"
import { viewCallbackSelector, viewSelector } from "../recoil/atoms/view"
import useServerWatchParty from "./useServerWatchParty"
import { useCallback } from "react"
import useMainVideoScaling from "../hooks/useMainVideoScaling"
import { rtcAutoplayFailedState } from "../recoil/atoms/rtcAutoplayFailed"
import { UplinkStatusAtom } from "../recoil/atoms/remoteVideoStreamType"
import { rtmpStreamsSelector } from "../recoil/atoms/rtmpStreams"
import { ActionType } from "../constants/enums"
import { disableMediaAtom, disableVideoAtom } from "../recoil/atoms/rtc"
import { cameraPermissionStateAtom, microphonePermissionStateAtom, settingsMuteSelector } from "../recoil/atoms/settings"
import { DevicePermissionState } from "../components/settings/settings.interfaces"
import useCustomResetRecoilState from "../hooks/useCustomResetRecoilState"
import { whiteboardShareUserAtom } from "../recoil/atoms/whiteboard"

interface MediaEventCBProps {
  onMediaConnected: (response: ConnectingResponse) => void
  onMediaDisconnected: (response: ConnectingResponse) => void
  onPublished: (response: PublishingResponse) => void
  onScreenShareStarted: (response: ConnectingResponse) => void
  onScreenShareStopped: (response: ConnectingResponse) => void
  onWhiteboardShareStarted: (response: ConnectingResponse) => void
  onWhiteboardShareStopped: (response: ConnectingResponse) => void
  onRTMPStarted: (response: ConnectingResponse) => void
  onRTMPStopped: (response: ConnectingResponse) => void
  onFail: (response: ConnectingResponse) => void
  onNackRemoteStatusChanged: (response: NackRemoteStatusChangeResponse) => void
  onUplinkStatusChanged: (response: UplinkStatusChangeResponse) => void
  onAutoplayFailed: () => void
  onDisableVideoChanged: (bValue: boolean) => void
  onDisableMediaChanged: (bValue: boolean) => void
  onCameraPermissionChanged: (state) => void 
  onMicPermissionChanged: (state) => void
}

const logger = new ConsoleLogger()

export const useMediaEventCB = (): MediaEventCBProps => {
  const setActiveSpeaker = useSetRecoilState(activeSpeakerSelector)
  const setUsers = useSetRecoilState(userIdsSelector)
  const setView = useSetRecoilState(viewSelector)
  const setScreenShareUser = useSetRecoilState(screenShareUserSelector)
  const setWhiteboardShareUser = useSetRecoilState(whiteboardShareUserAtom)
  const setJoinButtonLoading = useSetRecoilState(joinButtonLoadingSelector)
  const setRtcAutoplayFailedState = useSetRecoilState(rtcAutoplayFailedState)
  const setUplinkStatus = useSetRecoilState(UplinkStatusAtom)
  const setRtmpStreams = useSetRecoilState(rtmpStreamsSelector)
  const setDisableVideo = useSetRecoilState(disableVideoAtom)
  const setDisableMedia = useSetRecoilState(disableMediaAtom)
  const setMicrophonePermissionState = useSetRecoilState(microphonePermissionStateAtom)
  const setCameraPermissionState = useSetRecoilState(cameraPermissionStateAtom)
  const localUserCallbackProps = useRecoilValue(localUserCallbackSelector)
  const setIsMutedDevices = useSetRecoilState(settingsMuteSelector)
  const viewCallback = useRecoilValue(viewCallbackSelector) 
  const resetRecoilState = useCustomResetRecoilState()
  const MeetOnline = window.MeetOnline

  const { startServerWatchParty } = useServerWatchParty()

  const { handleNackStatusChange, reset } = useMainVideoScaling()

  const handleMediaConnect = async () => {
    if (MeetOnline?.client().maxHostsVisibleToAudience === true) {
      startServerWatchParty()
    }

    // Add other Users in the room
    // TODO: The users will now come in another message of type dataRequest.
    // setUsers({ name: "ADD", users: resp.data.users })

    // Set Current Active Speaker from MOG
    let activeSpeakerUid = MeetOnline?.client().maxHostsVisibleToAudience
      ? MeetOnline?.client().maxHostsVisibleToAudience
      : ""
    setActiveSpeaker({ uid: activeSpeakerUid })

    // check if user is already leaved or in bye page, if yes - return
    const currentView = await viewCallback.getCurrentView()
    if(currentView === Views.BYE) {
      resetRecoilState() // clean all state
      return
    }

    //Add Prev Chat History
    // TODO: The chats history will now come in another message of type dataRequest.
    if (IS_RECORDING_VIEW) {
      setView(Views.RECORDING_VIEW)
    } else {
      setView(Views.MEETING_ROOM)
    }

    // Later dummyBanners will come from the server
    // setBanners({ name: "ADD", banners: dummyBanners })

    setJoinButtonLoading(false)
  }

  const handleMediaOnMediaConnected = (response: ConnectingResponse) => {
    logger.log("CB----m1--RTC USER CONNECTED--", response)

    setUsers({
      name: "UPDATE",
      userId: response.uid,
      attributes: {
        mediaConnected: true,
        micEnabled: response.micEnabled,
        camEnabled: response.camEnabled,
        chatColor: chroma.random().hex()
      }
    })
    handleMediaConnect()
  }

  const handleMediaOnMediaDisconnected = (response: ConnectingResponse) => {
    logger.log("CB----m2--RTC USER DISCONNECTED--", response)
    reset()
    setUsers({
      name: "UPDATE",
      userId: response.uid,
      attributes: {
        mediaConnected: false,
        micEnabled: false,
        camEnabled: false
      }
    })
  }

  const handleMediaOnPublished = (response: PublishingResponse) => {
    logger.log("Client-CB----m3---onPublished-", response)
    setUsers({
      name: "UPDATE",
      userId: response.uid,
      attributes: {
        mediaConnected: true,
        micEnabled: response.hasAudio,
        camEnabled: response.hasVideo
      }
    })
  }

  const handleMediaOnScreenShareStarted = (response: ConnectingResponse) => {
    logger.log("CB----m5----onScreenShareStarted---", response)
    setScreenShareUser(response)
    let associatedUserId = response.uid?.replace("ss", "").trim()
    setUsers({
      name: "UPDATE",
      userId: associatedUserId,
      attributes: { screenShareEnabled: true }
    })
  }

  const handleMediaOnScreenShareStopped = (response: ConnectingResponse) => {
    logger.log("CB----m5----onScreenShareStopped---", response)
    setScreenShareUser(null)
    let associatedUserId = response.uid?.replace("ss", "").trim()
    setUsers({
      name: "UPDATE",
      userId: associatedUserId,
      attributes: { screenShareEnabled: false }
    })
  }

  const handleMediaOnWhiteboardShareStarted = (response: ConnectingResponse) => {
    console.log("CB----m5----onWhiteboardShareStarted---", response)
    logger.log("CB----m5----onWhiteboardShareStarted---", response)
    console.log("setting whiteboard share user", response, "because handleMediaOnWhiteboardShareStarted was called")
    setWhiteboardShareUser({uid: response.uid})
    let associatedUserId = response.uid?.replace("ws", "").trim()
    setUsers({
      name: "UPDATE",
      userId: associatedUserId,
      attributes: { whiteboardShareEnabled: true }
    })
  }

  const handleMediaOnWhiteboardShareStopped = (response: ConnectingResponse) => {
    logger.log("CB----m5----onWhiteboardShareStopped---", response)
    console.log("setting whiteboard share user", response, "because handleMediaOnWhiteboardShareStopped was called")
    setWhiteboardShareUser(null)
    let associatedUserId = response.uid?.replace("ws", "").trim()
    setUsers({
      name: "UPDATE",
      userId: associatedUserId,
      attributes: { whiteboardShareEnabled: false }
    })
  }

  const handleMediaOnRTMPStarted = (response: ConnectingResponse) => {
    logger.log("handleMediaOnRTMPStarted---", response)
    if (response.type === "video") {
      setRtmpStreams({
        action: ActionType.ADD,
        payload: response
      })
    }
  }

  const handleMediaOnRTMPStopped = (response: ConnectingResponse) => {
    logger.log("handleMediaOnRTMPStopped---", response)

    if (response.uid === "ALL") {
      // remove all the rtmp. this happen when user leave agora meeting
      setRtmpStreams({ action: ActionType.RESET })
    } else {
      setRtmpStreams({
        action: ActionType.DELETE,
        payload: response
      })
    }
  }

  const handleMediaOnFail = (response: ConnectingResponse) => {
    logger.log("CB----m9---onFail-", response)
  }

  const handleNackRemoteStatusChange = useCallback(
    ({ remoteStatus }: NackRemoteStatusChangeResponse) => {
      handleNackStatusChange(remoteStatus)
    },
    [handleNackStatusChange]
  )

  const handleAutoplayFailed = useCallback(() => {
    setRtcAutoplayFailedState(true)
  }, [setRtcAutoplayFailedState])

  const handleUplinkStatusChanged = useCallback(
    ({ uplinkStatus }: UplinkStatusChangeResponse) => {
      setUplinkStatus(uplinkStatus)
    },
    [setUplinkStatus]
  )

  const onDisableVideoChanged = useCallback((bValue: boolean) => {
    setDisableVideo(bValue)
    },
    [setDisableVideo]
  )

  const onDisableMediaChanged = useCallback((bValue: boolean) => {
    setDisableMedia(bValue)
    },
    [setDisableMedia]
  )
  
  const onCameraPermissionChanged = useCallback(async (value) => {
    setCameraPermissionState(value)
    
    // turn off the camera if permission not granted
    const localUser = await localUserCallbackProps.handleReturnData()
    if(localUser?.camEnabled && value !== DevicePermissionState.granted) {
      await MeetOnline.client()?.toggleCam?.(localUser?.camEnabled)
      setIsMutedDevices({ video: true })
    }

  }, [MeetOnline, localUserCallbackProps, setCameraPermissionState, setIsMutedDevices])
  
  const onMicPermissionChanged = useCallback(async (value) => {
    setMicrophonePermissionState(value)

    // turn off the mic if permission not granted
    const localUser = await localUserCallbackProps.handleReturnData()
    if(localUser?.micEnabled && value !== DevicePermissionState.granted) {
      await MeetOnline.client()?.toggleMic?.(localUser?.micEnabled)
      setIsMutedDevices({ video: true })
    }

  }, [MeetOnline, localUserCallbackProps, setIsMutedDevices, setMicrophonePermissionState])


  return {
    onMediaConnected: handleMediaOnMediaConnected,
    onMediaDisconnected: handleMediaOnMediaDisconnected,
    onPublished: handleMediaOnPublished,
    onScreenShareStarted: handleMediaOnScreenShareStarted,
    onScreenShareStopped: handleMediaOnScreenShareStopped,
    onWhiteboardShareStarted: handleMediaOnWhiteboardShareStarted,
    onWhiteboardShareStopped: handleMediaOnWhiteboardShareStopped,
    onRTMPStarted: handleMediaOnRTMPStarted,
    onRTMPStopped: handleMediaOnRTMPStopped,
    onFail: handleMediaOnFail,
    onNackRemoteStatusChanged: handleNackRemoteStatusChange,
    onUplinkStatusChanged: handleUplinkStatusChanged,
    onAutoplayFailed: handleAutoplayFailed,
    onDisableVideoChanged,
    onDisableMediaChanged,
    onCameraPermissionChanged,
    onMicPermissionChanged
  }
}
